import graphql from 'babel-plugin-relay/macro';

/**
 * Fragments
 */
export const USER_PROFILE_NAME_INLINE_FRAGMENT = graphql`
  fragment userProfileNameInlineFragment on UserProfile @inline {
    firstName
    lastName
  }
`;

export const USER_LOCATION_FRAGMENT = graphql`
  fragment userLocationFragment on UserProfile {
    city
    state
    countryId
  }
`;

export const USER_PROFILE_NAME_FRAGMENT = graphql`
  fragment userProfileNameFragment on UserProfile {
    firstName
    lastName
  }
`;

export const USER_PROFILE_AVAILABILITY_FRAGMENT = graphql`
  fragment userProfileAvailabilityFragment on UserProfile {
    hasAvailability
    sessionDuration
  }
`;

export const USER_PROFILE_BASE_INLINE_FRAGMENT = graphql`
  fragment userProfileBaseInlineFragment on UserProfile @inline {
    id
    email
    externalRef
    countryId
    timeZone
    photoUrls
  }
`;

export const USER_PROFILE_BASE_FRAGMENT = graphql`
  fragment userProfileBaseFragment on UserProfile {
    id
    email
    externalRef
    countryId
    timeZone
    photoUrls
    phone
    languages
    setupComplete
    googleCredentials
    gender
    ethnicity
  }
`;

export const USER_PROFILE_TALK_DETAILS_FRAGMENT = graphql`
  fragment userProfileTalkDetailsFragment on ClientDetails {
    isTalk
    isLive
  }
`;

export const USER_PROFILE_APPLICATION_STATUS_FRAGMENT = graphql`
  fragment userProfileApplicationStatusFragment on UserProfileDetailConnect {
    onboardingStatus
    applicationStatus
    documentStatus
    profileStatus
    setupCompletedAt
    onboardingApprovedAt
    applicationApprovedAt
  }
`;

export const USER_PROFILE_DETAIL_FRAGMENT = graphql`
  fragment userProfileDetailFragment on UserProfileDetailConnect {
    id
    type
    roleId
    profileInformation
    profileSettings
    defaultSessionPassword
    zoomDetails
    clientLocations
    preferredVideoType
    googleCalendarSync
    signedAgreement
    setupCompletedAt
    specialities {
      approaches {
        id
        name
      }
      areasOfExpertise {
        id
        name
      }
      trackingOnlySpecialities {
        id
        name
      }
      clientGroups {
        id
        name
      }
    }
    profileQuestionResponses {
      id
      answer
      profileQuestion {
        id
        translationKey
        question
      }
    }
    ...userProfileApplicationStatusFragment
  }
`;

export const USER_AVAILABILITY_CONFIG_FRAGMENT = graphql`
  fragment userAvailabilityConfigFragment on UserAvailabilityConfig {
    id
    duration
  }
`;

export const USER_AVAILABILITY_CONFIG_QUERY_FRAGMENT = graphql`
  fragment userAvailabilityConfigQueryFragment on Query
    @refetchable(queryName: "userAvailabilitiesConfigRefetchableQuery") {
    findAvailabilityConfig(input: $input) {
      ...userAvailabilityConfigFragment
    }
  }
`;

/**
 * Mutations
 */
export const USER_UPDATE_USER_PROFILE_PASSWORD_MUTATION = graphql`
  mutation userUpdateUserProfilePasswordMutation($id: ID!, $input: UpdateUserProfilePassword!) {
    updateUserProfilePassword(id: $id, input: $input)
  }
`;

export const USER_DELETE_USER_AVAILABILITY = graphql`
  mutation userDeleteUserAvailabilityMutation($connections: [ID!]!, $id: ID!) {
    deleteUserAvailability(id: $id) {
      id @deleteEdge(connections: $connections)
    }
  }
`;

export const USER_CREATE_AVAILABILITY = graphql`
  mutation userCreateAvailabilityMutation($input: AvailabilityCreateInput!) {
    createAvailability(input: $input)
  }
`;

export const UPDATE_USER_PROFILE_MUTATION = graphql`
  mutation userUpdateUserProfileMutation($id: ID!, $input: UserProfileUpdateInput!) {
    updateUserProfile(id: $id, input: $input) {
      userProfile {
        ...userProfileNameFragment
        ...userProfileBaseFragment
        ...userLocationFragment
      }
    }
  }
`;

export const UPDATE_USER_PROFILE_DETAIL_MUTATION = graphql`
  mutation userUpdateUserProfileDetailConnectMutation($id: ID!, $input: UserProfileDetailConnectUpdateInput!) {
    updateUserProfileDetailConnectWithSetupCompleteCheck(id: $id, input: $input) {
      userProfileDetailConnect {
        ...userProfileDetailFragment
      }
    }
  }
`;

export const USER_COMPLETE_ZOOM_OAUTH = graphql`
  mutation userCompleteZoomOAuthMutation($input: CompleteZoomOauthInput!) {
    completeZoomOauth(input: $input) {
      details {
        ...userProfileDetailFragment
      }
    }
  }
`;

export const USER_REVOKE_ZOOM_AUTH = graphql`
  mutation userRevokeZoomAuthMutation($userId: ID!) {
    revokeZoomAccess(userId: $userId) {
      details {
        ...userProfileDetailFragment
      }
    }
  }
`;

export const USER_COMPLETE_APPLICATION_VERIFICATION = graphql`
  mutation userCompleteApplicationVerificationMutation($userId: ID!) {
    completeApplicationVerification(userId: $userId)
  }
`;

export const USER_CREATE_UPLOAD_PHOTO_SIGNED_URL_MUTATION = graphql`
  mutation userCreateUploadPhotoSignedUrlMutation($filesize: Int!) {
    createUploadPhotoSignedUrl(filesize: $filesize) {
      signedUrl
      photoUrl
    }
  }
`;

export const USER_SET_AVAILABILITY_CONFIG_MUTATION = graphql`
  mutation userSetAvailabilityConfigMutation($input: AvailabilityConfigInput!) {
    setAvailabilityConfig(input: $input)
  }
`;

/**
 * Queries
 */
export const USER_PROFILE_QUERY = graphql`
  query userProfileQuery($id: ID!, $skip: Boolean!) {
    userProfile(id: $id) @skip(if: $skip) {
      isExemptFromAgreement
      ...userProfileNameFragment
      ...userProfileBaseFragment
      ...userProfileNameInlineFragment
      ...userProfileBaseInlineFragment
      ...userLocationFragment
      ...userProfileAvailabilityFragment
      account {
        ...accountBaseInlineFragment
        ...accountBaseFragment
        accountDetailConnect {
          ...accountConnectDetailFragment
          ...accountConnectDetailInlineFragment
        }
      }
      details {
        ...userProfileDetailFragment
      }
      channels {
        ...channelMemberDetailFragment
      }
      clientDetails {
        ...userProfileTalkDetailsFragment
      }
    }
  }
`;

export const USER_AVAILABILITY_CONFIG_QUERY = graphql`
  query userAvailabilityConfigQuery($input: FindAvailabilityConfigInput!) {
    ...userAvailabilityConfigQueryFragment
  }
`;
